import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Service for parent-child messaging
 */

/**
 * Send a message from parent to child or child to parent
 * @param {string} senderId - Sender user ID (parent or child)
 * @param {string} receiverId - Receiver user ID (child or parent)
 * @param {string} message - Message content
 * @param {string} senderType - 'parent' or 'student'
 * @returns {Promise<Object>} Message object
 */
export const sendMessage = async (senderId, receiverId, message, senderType) => {
  try {
    if (!message || !message.trim()) {
      throw new Error('Message cannot be empty');
    }

    // Verify relationship
    if (senderType === 'parent') {
      const parentRef = doc(db, 'users', senderId);
      const parentSnap = await getDoc(parentRef);
      const parentData = parentSnap.data();
      if (!parentData?.linkedChildren?.includes(receiverId)) {
        throw new Error('You can only message your linked children');
      }
    } else {
      const childRef = doc(db, 'users', receiverId);
      const childSnap = await getDoc(childRef);
      const childData = childSnap.data();
      if (!childData?.linkedParents?.includes(senderId)) {
        throw new Error('You can only message your linked parents');
      }
    }

    // Create conversation ID (sorted IDs to ensure consistency)
    const conversationId = [senderId, receiverId].sort().join('_');

    // Add message to conversation
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    const messageData = {
      senderId,
      receiverId,
      senderType,
      message: message.trim(),
      read: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const messageRef = await addDoc(messagesRef, messageData);

    // Update conversation metadata
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    if (conversationSnap.exists()) {
      await updateDoc(conversationRef, {
        lastMessage: message.trim(),
        lastMessageAt: Timestamp.now(),
        lastMessageBy: senderId,
        updatedAt: Timestamp.now()
      });
    } else {
      // Create conversation document
      await updateDoc(conversationRef, {
        parentId: senderType === 'parent' ? senderId : receiverId,
        childId: senderType === 'parent' ? receiverId : senderId,
        lastMessage: message.trim(),
        lastMessageAt: Timestamp.now(),
        lastMessageBy: senderId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      }, { merge: true });
    }

    return {
      id: messageRef.id,
      ...messageData
    };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Get conversation between parent and child
 * @param {string} parentId - Parent user ID
 * @param {string} childId - Child user ID
 * @param {number} limitCount - Number of messages to retrieve (default: 50)
 * @returns {Promise<Array>} Array of messages
 */
export const getConversation = async (parentId, childId, limitCount = 50) => {
  try {
    const conversationId = [parentId, childId].sort().join('_');
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    
    const messagesQuery = query(
      messagesRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(messagesQuery);
    const messages = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Reverse to show oldest first
    return messages.reverse();
  } catch (error) {
    console.error('Error getting conversation:', error);
    return [];
  }
};

/**
 * Subscribe to conversation updates
 * @param {string} parentId - Parent user ID
 * @param {string} childId - Child user ID
 * @param {Function} callback - Callback function to receive updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToConversation = (parentId, childId, callback) => {
  try {
    const conversationId = [parentId, childId].sort().join('_');
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    
    const messagesQuery = query(
      messagesRef,
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    return onSnapshot(messagesQuery, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Reverse to show oldest first
      callback(messages.reverse());
    }, (error) => {
      console.error('Error subscribing to conversation:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up conversation subscription:', error);
    return () => {};
  }
};

/**
 * Mark messages as read
 * @param {string} parentId - Parent user ID
 * @param {string} childId - Child user ID
 * @param {string} readerId - ID of user marking as read
 * @returns {Promise<void>}
 */
export const markMessagesAsRead = async (parentId, childId, readerId) => {
  try {
    const conversationId = [parentId, childId].sort().join('_');
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    
    // Get unread messages sent to the reader
    const unreadQuery = query(
      messagesRef,
      where('receiverId', '==', readerId),
      where('read', '==', false)
    );

    const snapshot = await getDocs(unreadQuery);
    const updatePromises = snapshot.docs.map(doc => 
      updateDoc(doc.ref, {
        read: true,
        readAt: Timestamp.now()
      })
    );

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

/**
 * Get all conversations for a parent
 * @param {string} parentId - Parent user ID
 * @returns {Promise<Array>} Array of conversation summaries
 */
export const getParentConversations = async (parentId) => {
  try {
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('parentId', '==', parentId),
      orderBy('lastMessageAt', 'desc')
    );

    const snapshot = await getDocs(conversationsQuery);
    const conversations = [];

    for (const docSnap of snapshot.docs) {
      const convData = docSnap.data();
      
      // Get child info
      const childRef = doc(db, 'users', convData.childId);
      const childSnap = await getDoc(childRef);
      const childData = childSnap.exists() ? childSnap.data() : null;

      // Get unread count
      const messagesRef = collection(db, 'conversations', docSnap.id, 'messages');
      const unreadQuery = query(
        messagesRef,
        where('receiverId', '==', parentId),
        where('read', '==', false)
      );
      const unreadSnapshot = await getDocs(unreadQuery);

      conversations.push({
        id: docSnap.id,
        childId: convData.childId,
        childName: childData?.name || childData?.email?.split('@')[0] || 'Student',
        childEmail: childData?.email,
        lastMessage: convData.lastMessage,
        lastMessageAt: convData.lastMessageAt,
        lastMessageBy: convData.lastMessageBy,
        unreadCount: unreadSnapshot.size,
        createdAt: convData.createdAt
      });
    }

    return conversations;
  } catch (error) {
    console.error('Error getting parent conversations:', error);
    return [];
  }
};

/**
 * Get all conversations for a child
 * @param {string} childId - Child user ID
 * @returns {Promise<Array>} Array of conversation summaries
 */
export const getChildConversations = async (childId) => {
  try {
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('childId', '==', childId),
      orderBy('lastMessageAt', 'desc')
    );

    const snapshot = await getDocs(conversationsQuery);
    const conversations = [];

    for (const docSnap of snapshot.docs) {
      const convData = docSnap.data();
      
      // Get parent info
      const parentRef = doc(db, 'users', convData.parentId);
      const parentSnap = await getDoc(parentRef);
      const parentData = parentSnap.exists() ? parentSnap.data() : null;

      // Get unread count
      const messagesRef = collection(db, 'conversations', docSnap.id, 'messages');
      const unreadQuery = query(
        messagesRef,
        where('receiverId', '==', childId),
        where('read', '==', false)
      );
      const unreadSnapshot = await getDocs(unreadQuery);

      conversations.push({
        id: docSnap.id,
        parentId: convData.parentId,
        parentName: parentData?.name || 'Parent',
        parentEmail: parentData?.email,
        lastMessage: convData.lastMessage,
        lastMessageAt: convData.lastMessageAt,
        lastMessageBy: convData.lastMessageBy,
        unreadCount: unreadSnapshot.size,
        createdAt: convData.createdAt
      });
    }

    return conversations;
  } catch (error) {
    console.error('Error getting child conversations:', error);
    return [];
  }
};

